import { qs, qsa, on } from '../lib/dom.js';
import { num, monthlyPayment } from '../lib/format.js';

// Fallback ONLY. The real rate comes from the `data-apr` attribute the server
// prints on the component from the `autoplaza_apr` setting (atpl_finance_apr()).
// Hardcoding it here made the calculator drift from the server-rendered
// headline rate on the car page, and made the admin setting inert.
const APR_FALLBACK = 0.085;

export function initFinanceCalc(root = document) {
  qsa('[data-component="finance-calc"]', root).forEach((calc) => {
    const priceSlider = qs('[data-role="price-slider"]', calc);
    const downSlider = qs('[data-role="down-slider"]', calc);
    const termSlider = qs('[data-role="term-slider"]', calc);
    const priceVal = qs('[data-role="price-value"]', calc);
    const downVal = qs('[data-role="down-value"]', calc);
    const termVal = qs('[data-role="term-value"]', calc);
    // Multiple "monthly" outputs exist now: the big desktop number AND the
    // mobile compact stats block both want the figure.
    const monthly = qsa('[data-role="monthly"]', calc);
    const loanAmount = qs('[data-role="loan-amount"]', calc);
    const expTerm = qs('[data-role="exp-term"]', calc);
    const expTotal = qs('[data-role="exp-total"]', calc);
    const expPrice = qs('[data-role="exp-price"]', calc);
    if (!priceSlider || !termSlider || monthly.length === 0) return;

    // Cap down payment at 60% of the price - banks/leasing partners typically
    // require at least 40% to be financed for the deal to make sense.
    const DOWN_CAP_RATIO = 0.6;

    // Linked "Obține preaprobare" modal (frontpage/catalog/credit). Optional -
    // the single-car listing finance card has no preapproval modal, so guard.
    const section = calc.closest('section') || document;
    const preappModal =
      qs('#preapproval-modal', section) || document.getElementById('preapproval-modal');

    // Linked "Caută după rata lunară" button → catalog filtered to cars at or
    // below the current monthly rate. Keep its base (catalog) URL and rewrite
    // ?rateMax on every recalc so the link always reflects the live figure.
    // Optional: the single-car listing finance card has no such button.
    const rateLink = qs('[data-role="rate-search"]', calc);
    const rateBase = rateLink ? rateLink.getAttribute('href') : '';

    // On the single-car listing the price slider is LOCKED (disabled) and a
    // `data-price` attribute carries the EXACT integer car price. An
    // <input type="range"> snaps its value to the nearest `step` (500), so
    // reading priceSlider.value would round the price. When data-price is
    // present, use it verbatim for all price math and the displayed figure.
    // On the homepage/catalog calculator the slider is draggable and there is
    // no data-price, so lockedPrice is null and behaviour is unchanged.
    const lockedPrice = calc.dataset.price ? +calc.dataset.price : null;

    // Annual rate as a fraction: the server prints it as a percentage
    // (8.5), so divide by 100. A 0 is legitimate (0% promo) and
    // monthlyPayment() handles it, hence `>= 0` rather than `> 0`.
    const aprAttr = parseFloat(calc.dataset.apr);
    const apr = Number.isFinite(aprAttr) && aprAttr >= 0 ? aprAttr / 100 : APR_FALLBACK;

    // Live calculator snapshot, kept in sync by recalc().
    const state = { price: 0, avans: 0, term: 0, monthly: 0, credit: 0 };

    function recalc() {
      const price = lockedPrice != null ? lockedPrice : +priceSlider.value;
      const downCap = Math.floor(price * DOWN_CAP_RATIO);
      const down = downSlider ? Math.min(+downSlider.value, downCap) : 0;
      const term = +termSlider.value;
      const principal = Math.max(0, price - down);
      const m = monthlyPayment(principal, apr, term);
      const total = Math.round(m * term);
      // The result box shows the TOTAL INTEREST (dobânda) — total repaid minus the
      // financed principal — NOT the car price + interest (client, 2026-09).
      const interest = Math.max(0, total - principal);
      if (priceVal) priceVal.textContent = num(price);
      if (downSlider) {
        downSlider.max = String(downCap);
        if (+downSlider.value > downCap) downSlider.value = String(downCap);
        if (downVal) downVal.textContent = num(+downSlider.value);
      }
      if (termVal) termVal.textContent = String(term);
      const monthlyText = num(Math.round(m));
      monthly.forEach((el) => { el.textContent = monthlyText; });
      if (loanAmount) loanAmount.textContent = num(principal);
      if (expTerm) expTerm.textContent = term;
      if (expTotal) expTotal.textContent = num(interest) + ' €';
      if (expPrice) expPrice.textContent = num(price) + ' €';

      // Snapshot the live values for the preapproval lead. monthly/credit use the
      // SAME computation as the displayed figures above (no re-derivation).
      state.price = price;
      state.avans = downSlider ? +downSlider.value : 0;
      state.term = term;
      state.monthly = Math.round(m);
      state.credit = principal;

      // Point the "Caută după rata lunară" button at the catalog filtered to
      // cars whose monthly rate is at or below the current figure (?rateMax).
      if (rateLink && rateBase) {
        try {
          const u = new URL(rateBase, window.location.origin);
          u.searchParams.set('rateMax', String(state.monthly));
          rateLink.href = u.toString();
        } catch (e) { /* keep the plain catalog link as fallback */ }
      }
    }

    [priceSlider, downSlider, termSlider].filter(Boolean).forEach((el) => {
      on(el, 'input', recalc);
    });
    recalc();

    // When the preapproval modal opens, copy the current calculator values into
    // its hidden inputs so the submitted lead reflects exactly what the user sees.
    if (preappModal) {
      on(preappModal, 'modal:open', () => {
        const set = (name, value) => {
          const input = qs(`[name="${name}"]`, preappModal);
          if (input) input.value = value;
        };
        set('price', state.price);
        set('avans', state.avans);
        set('term', state.term);
        set('monthly', state.monthly);
        set('credit', state.credit);
      });
    }
  });
}
